/**
 * Admin-only diagnostic route: throws a controlled error so we can verify
 * source-map upload to Sentry is working. Lives under /_authenticated/admin/
 * so the existing admin route guard restricts access.
 *
 * Usage:
 *   1. Visit /admin/sentry-test as an admin.
 *   2. Click "Throw client error" — Sentry should receive an event tagged
 *      with the current release.
 *   3. Open the event in Sentry; the stack frame should resolve to this
 *      source file with the original line numbers (proves source maps
 *      uploaded against the matching release).
 */
import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_authenticated/admin/sentry-test")({
  component: SentryTestPage,
});

function triggerControlledError(): never {
  // Distinctive message so it's easy to find in Sentry.
  throw new Error("[forexmind] controlled Sentry source-map test error");
}

function SentryTestPage() {
  const [armed, setArmed] = React.useState(false);
  const release =
    (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || "(release not set)";

  // Re-render-time throw so React's error boundary captures it; Sentry
  // forwards uncaught render errors via the global error handler.
  if (armed) {
    triggerControlledError();
  }

  return (
    <div className="container max-w-2xl py-10">
      <Card>
        <CardHeader>
          <CardTitle>Sentry Source-Map Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Throws a controlled client error tagged with the current build
            release. Confirm the event appears in Sentry with a readable
            stack trace pointing back to <code>src/routes/_authenticated/admin/sentry-test.tsx</code>.
          </p>
          <p className="text-xs text-muted-foreground">
            Current release: <code>{release}</code>
          </p>
          <Button
            variant="destructive"
            onClick={() => setArmed(true)}
            data-testid="sentry-test-throw"
          >
            Throw client error
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
